<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { fetchPublicationStatus, type PublicationStatus } from '@/api/admin';
import { AlertTriangle, X } from 'lucide-vue-next';

/**
 * P3a — in-app reminder for super_admins when the monthly publication
 * target day has passed and no publication exists for the current
 * month. Auto-hides on publish (status refetch on page nav covers it).
 *
 * Mounted in AppShell so it appears across the platform for super_admin
 * users only. Tenant analysts never see this.
 */
const auth = useAuthStore();
const status = ref<PublicationStatus | null>(null);
const dismissed = ref(false);

const isSuperAdmin = computed(() => auth.user?.role === 'super_admin');
const visible = computed(() =>
  isSuperAdmin.value &&
  !dismissed.value &&
  status.value?.targetDayPassed &&
  !status.value?.currentMonthPublished,
);

onMounted(async () => {
  if (!isSuperAdmin.value || !auth.user?.orgId) return;
  try {
    status.value = await fetchPublicationStatus(auth.user.orgId);
  } catch {
    // Silent fail — banner is informational, not load-blocking.
  }
});
</script>

<template>
  <div
    v-if="visible"
    class="publication-reminder-banner flex items-center justify-between gap-3 px-4 py-2.5 bg-severity-medium/10 border-b border-severity-medium/30"
    data-qa-publication-banner
  >
    <div class="flex items-center gap-2 text-sm">
      <AlertTriangle class="w-4 h-4 text-severity-medium" />
      <span class="text-text-primary">
        Monthly refresh hasn't published yet for this month.
      </span>
    </div>
    <div class="flex items-center gap-2">
      <RouterLink
        to="/admin/scoring"
        class="text-sm font-medium text-accent-blue hover:underline"
        data-qa-publication-banner-cta
      >
        Publish now →
      </RouterLink>
      <button
        class="p-1 rounded hover:bg-surface-700 cwr-motion"
        :title="`Dismiss until next page load`"
        @click="dismissed = true"
      >
        <X class="w-3.5 h-3.5 text-text-muted" />
      </button>
    </div>
  </div>
</template>
